/**
 * Countdown Timer Utilities
 *
 * Provides countdown calculation, formatting, and urgency level determination
 * for calendar events on the map view.
 */

export interface CountdownData {
  timeRemaining: number; // Milliseconds until event
  formattedCountdown: string; // Human-readable format like "2h 30m" or "3 days"
  urgencyLevel: "far" | "soon" | "imminent" | "now";
  isHappening: boolean; // True if event is currently happening
  isPast: boolean; // True if event has ended
}

export interface CountdownConfig {
  updateInterval: "second" | "minute" | "static" | "smart";
  format: "relative" | "precise" | "hybrid" | "contextual";
  urgencyThresholds: {
    imminent: number; // < 1 hour
    soon: number; // < 24 hours
    far: number; // >= 24 hours
  };
}

// Default configuration - FULL TIMERS WITH SECOND COUNTDOWNS
export const DEFAULT_COUNTDOWN_CONFIG: CountdownConfig = {
  updateInterval: "second", // Update every second for live ticking
  format: "precise", // Show full HH:MM:SS format
  urgencyThresholds: {
    imminent: 60 * 60 * 1000, // 1 hour
    soon: 24 * 60 * 60 * 1000, // 24 hours
    far: Number.MAX_SAFE_INTEGER, // Everything else
  },
};

/**
 * Calculate countdown data for an event
 */
export const calculateCountdown = (
  eventStartDate: Date,
  eventEndDate?: Date,
  config: CountdownConfig = DEFAULT_COUNTDOWN_CONFIG,
): CountdownData => {
  const now = Date.now();
  const startTime = eventStartDate.getTime();
  const endTime = eventEndDate?.getTime() || startTime;

  const timeRemaining = startTime - now;
  const isHappening = now >= startTime && now <= endTime;
  const isPast = now > endTime;

  // Determine urgency level
  let urgencyLevel: CountdownData["urgencyLevel"];
  if (isHappening) {
    urgencyLevel = "now";
  } else if (isPast) {
    urgencyLevel = "far";
  } else if (timeRemaining < config.urgencyThresholds.imminent) {
    urgencyLevel = "imminent";
  } else if (timeRemaining < config.urgencyThresholds.soon) {
    urgencyLevel = "soon";
  } else {
    urgencyLevel = "far";
  }

  // Format countdown based on config
  const formattedCountdown = formatCountdown(
    timeRemaining,
    config.format,
    isHappening,
    isPast,
  );

  return {
    timeRemaining,
    formattedCountdown,
    urgencyLevel,
    isHappening,
    isPast,
  };
};

/**
 * Format milliseconds into human-readable countdown string
 */
export const formatCountdown = (
  milliseconds: number,
  format: CountdownConfig["format"] = "hybrid",
  isHappening = false,
  isPast = false,
): string => {
  if (isHappening) {
    return "NOW";
  }

  if (isPast) {
    return "PAST";
  }

  const totalSeconds = Math.floor(Math.abs(milliseconds) / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  switch (format) {
    case "precise":
      // Exact time with live ticking: "02:45:30"
      return formatPreciseCountdown(hours + days * 24, minutes, seconds);

    case "relative":
      // Human-readable relative: "3 days", "5 hours"
      return formatRelativeCountdown(days, hours, minutes);

    case "contextual":
      // Different formats based on time range
      return formatContextualCountdown(days, hours, minutes);

    case "hybrid":
    default:
      // Smart hybrid: "3d", "5h", "30m"
      return formatHybridCountdown(days, hours, minutes, seconds);
  }
};

/**
 * Format as precise countdown: "02:45:30" or "2d 14:30:15" for longer durations
 */
const formatPreciseCountdown = (
  hours: number,
  minutes: number,
  seconds: number,
): string => {
  const pad = (n: number) => n.toString().padStart(2, "0");

  // If more than 24 hours, show days separately
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${pad(remainingHours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

/**
 * Format as relative countdown: "3 days", "5 hours", "30 minutes"
 */
const formatRelativeCountdown = (
  days: number,
  hours: number,
  minutes: number,
): string => {
  if (days > 0) {
    return `${days} ${days === 1 ? "day" : "days"}`;
  }
  if (hours > 0) {
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  }
  return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
};

/**
 * Format as contextual countdown: "in 3 days" vs "45 min"
 */
const formatContextualCountdown = (
  days: number,
  hours: number,
  minutes: number,
): string => {
  if (days > 0) {
    return `in ${days}d`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes} min`;
  }
  return "soon";
};

/**
 * Format as hybrid countdown: "3d", "5h", "30m"
 * Most compact format - best for map markers
 */
const formatHybridCountdown = (
  days: number,
  hours: number,
  minutes: number,
  seconds: number,
): string => {
  if (days > 0) {
    return `${days}d`;
  }
  if (hours > 0) {
    return `${hours}h`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  // For < 1 minute, show seconds
  return `${seconds}s`;
};

/**
 * Get smart update interval based on time remaining
 * Returns interval in milliseconds
 */
export const getSmartUpdateInterval = (timeRemaining: number): number => {
  const absTime = Math.abs(timeRemaining);

  // < 1 hour: update every second
  if (absTime < 60 * 60 * 1000) {
    return 1000;
  }

  // < 1 day: update every minute
  if (absTime < 24 * 60 * 60 * 1000) {
    return 60 * 1000;
  }

  // >= 1 day: update every 5 minutes
  return 5 * 60 * 1000;
};

/**
 * Heat map color scale for countdown timers
 * Creates a smooth gradient from cool (far away) to hot (very close) colors
 */
interface HeatMapColor {
  threshold: number; // Time in milliseconds
  color: string; // Hex color
  borderColor: string; // Border/darker variant
  label: string; // Human-readable label
}

const HEAT_MAP_SCALE: HeatMapColor[] = [
  {
    threshold: 7 * 24 * 60 * 60 * 1000,
    color: "#0ea5e9",
    borderColor: "#0284c7",
    label: "7+ days",
  }, // Sky blue
  {
    threshold: 3 * 24 * 60 * 60 * 1000,
    color: "#06b6d4",
    borderColor: "#0891b2",
    label: "3-7 days",
  }, // Cyan
  {
    threshold: 1 * 24 * 60 * 60 * 1000,
    color: "#14b8a6",
    borderColor: "#0d9488",
    label: "1-3 days",
  }, // Teal
  {
    threshold: 12 * 60 * 60 * 1000,
    color: "#84cc16",
    borderColor: "#65a30d",
    label: "12-24 hours",
  }, // Lime
  {
    threshold: 6 * 60 * 60 * 1000,
    color: "#eab308",
    borderColor: "#ca8a04",
    label: "6-12 hours",
  }, // Yellow
  {
    threshold: 3 * 60 * 60 * 1000,
    color: "#f97316",
    borderColor: "#ea580c",
    label: "3-6 hours",
  }, // Orange
  {
    threshold: 1 * 60 * 60 * 1000,
    color: "#ea580c",
    borderColor: "#c2410c",
    label: "1-3 hours",
  }, // Deep orange
  {
    threshold: 30 * 60 * 1000,
    color: "#dc2626",
    borderColor: "#b91c1c",
    label: "30-60 min",
  }, // Red
  {
    threshold: 10 * 60 * 1000,
    color: "#b91c1c",
    borderColor: "#991b1b",
    label: "10-30 min",
  }, // Deep red
  { threshold: 0, color: "#991b1b", borderColor: "#7f1d1d", label: "0-10 min" }, // Crimson
];

/**
 * Get heat map color based on exact time remaining
 * Provides smooth color transitions for better visual feedback
 */
export const getHeatMapColor = (
  timeRemaining: number,
): { color: string; borderColor: string } => {
  // Special case: happening now (bright green indicates "live")
  if (timeRemaining <= 0) {
    return {
      color: "#22c55e", // Bright green
      borderColor: "#16a34a", // Dark green
    };
  }

  // Find the appropriate color from heat map scale
  for (const { threshold, color, borderColor } of HEAT_MAP_SCALE) {
    if (timeRemaining >= threshold) {
      return { color, borderColor };
    }
  }

  // Fallback to most urgent color (shouldn't reach here)
  return {
    color: "#991b1b",
    borderColor: "#7f1d1d",
  };
};

/**
 * Get countdown display color based on urgency level (legacy support)
 * @deprecated Use getHeatMapColor for better visual feedback
 */
export const getUrgencyColor = (
  urgencyLevel: CountdownData["urgencyLevel"],
): string => {
  switch (urgencyLevel) {
    case "now":
      return "#22c55e"; // Green - happening now
    case "imminent":
      return "#ef4444"; // Red - very soon
    case "soon":
      return "#f59e0b"; // Orange - upcoming
    case "far":
    default:
      return "#6366f1"; // Indigo - future
  }
};

/**
 * Get countdown border color for marker (legacy support)
 * @deprecated Use getHeatMapColor for better visual feedback
 */
export const getUrgencyBorderColor = (
  urgencyLevel: CountdownData["urgencyLevel"],
): string => {
  switch (urgencyLevel) {
    case "now":
      return "#16a34a"; // Darker green
    case "imminent":
      return "#dc2626"; // Darker red
    case "soon":
      return "#d97706"; // Darker orange
    case "far":
    default:
      return "#ffffff"; // White
  }
};

/**
 * Check if countdown should pulse/animate
 */
export const shouldPulse = (
  urgencyLevel: CountdownData["urgencyLevel"],
): boolean => {
  return urgencyLevel === "imminent" || urgencyLevel === "now";
};

/**
 * Format countdown for display in different contexts
 */
export const formatCountdownForContext = (
  countdown: CountdownData,
  context: "marker" | "badge" | "popup" | "list",
): string => {
  switch (context) {
    case "marker":
      // Show full timer in marker
      if (countdown.isHappening) return "NOW";
      if (countdown.isPast) return "PAST";
      return countdown.formattedCountdown;

    case "badge":
      // Full timer with icon for badge
      if (countdown.isHappening) return "▶ NOW";
      if (countdown.isPast) return "✓ DONE";
      return `⏱ ${countdown.formattedCountdown}`;

    case "popup":
      // Full timer for popup
      if (countdown.isHappening) return "Happening Now";
      if (countdown.isPast) return "Event Ended";
      return countdown.formattedCountdown;

    case "list":
      // Full timer for list view
      if (countdown.isHappening) return "NOW";
      if (countdown.isPast) return "PAST";
      return countdown.formattedCountdown;

    default:
      return countdown.formattedCountdown;
  }
};
